import React, { Suspense, useEffect, useState } from "react";
import lazyWithRetry from "../../utils/lazyWithRetry";
import { ChunkErrorBoundary } from "../errors";

// The form, its map and Google's map library load only when it's first
// opened, not with every page.
const NewOrderModal = lazyWithRetry(() => import("./NewOrderModal"));

const OPEN_EVENT = "chariot:new-order";
const START_AS = new Set(["repair_order", "estimate"]);

/**
 * Opens the New Order form from anywhere (the top bar's +, the board's
 * + New Order, a customer's New Estimate). `preset` (optional): `customer`,
 * a customer record to fill in, and `status`, 'estimate' or 'repair_order'
 * (the default). Anything else — a click event passed along — opens it blank.
 */
export const openNewOrder = (preset) => {
  const plain = preset && Object.getPrototypeOf(preset) === Object.prototype;
  const detail = plain
    ? { customer: preset.customer?.id ? preset.customer : null, status: START_AS.has(preset.status) ? preset.status : null }
    : null;
  window.dispatchEvent(new CustomEvent(OPEN_EVENT, { detail }));
};

/** Renders the New Order form when something asks for it. Mounted once, by AdminLayout. */
export default function NewOrderHost() {
  const [preset, setPreset] = useState(null); // null: closed
  useEffect(() => {
    // One form at a time: asking again while it's open keeps what's typed.
    const show = (e) => setPreset((open) => open || { ...(e.detail || {}) });
    window.addEventListener(OPEN_EVENT, show);
    return () => window.removeEventListener(OPEN_EVENT, show);
  }, []);
  if (!preset) return null;
  // The form renders into a portal, so this frame stays empty (and unseen)
  // unless its files couldn't load — then it holds the reload message.
  return (
    <div className="nom-host">
      <ChunkErrorBoundary>
        <Suspense fallback={null}>
          <NewOrderModal preset={preset} onClose={() => setPreset(null)} />
        </Suspense>
      </ChunkErrorBoundary>
    </div>
  );
}
